import { addDays, format, isSameDay } from "date-fns";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import type { AutoPublishState } from "../../hooks/useAutoPublish";
import type { SocialProviderAccount } from "../../hooks/useSocialProviderAccounts";
import { type AccountCadence, DEFAULT_PREFERRED_HOURS, getAccountCadence, getPreferredHoursForCadence } from "../publicationFlow/engine/types";
import { AUTO_POST } from "./autoPostName";

dayjs.extend(utc);
dayjs.extend(timezone);

/** Shared status copy. Surfaces choose the layout, never rebuild the wording. */
export const AUTO_POST_STATUS = {
  loading: `Loading ${AUTO_POST}`,
  checking: "Checking your plan",
  empty: "No posts scheduled",
  waitingForShorts: "Shorts will be scheduled when ready",
  unavailable: `${AUTO_POST} unavailable · Retry`,
  sourceBlocked: `${AUTO_POST} unavailable · Settings saved`,
} as const;

export const autoPostStateLabel = (on: boolean) => on ? "On" : "Off";
export const autoPostStateTooltip = (on: boolean) => `${AUTO_POST} <span class="text-slate-400">${autoPostStateLabel(on)}</span>`;
export const autoPostActiveProjectsLabel = (count: number) => `${AUTO_POST} · ${count} active ${count === 1 ? "project" : "projects"}`;

export const getAutoPublishSourceFailure = (taskStatus?: string): string | null =>
  taskStatus === "error" ? "Video failed" : taskStatus === "expired" ? "Video expired" : null;

export const autoPublishBusyLabel = ({ isSaving, isStarting = false }: { isSaving: boolean; isStarting?: boolean }, projectName?: string) =>
  isSaving ? `${isStarting ? "Starting" : "Updating"} ${AUTO_POST}${projectName ? ` for ${projectName}` : ""}…` : undefined;

/** What the switch does, while off, and what Schedule doesn't: it stays on for this project's later shorts.
 * Counted once the shorts are ready; before that (submit form, processing) the same line without a count. */
export const autoPublishSummary = (shortCount?: number) =>
  shortCount === 1 ? "Schedules the short, and any you add later"
    : shortCount ? `Schedules all ${shortCount} shorts, and any you add later` : "Schedules all shorts, and any you add later";

/** The submit form's line, before the project or any short exists: "any you add later" read there as
 * every future video (reading test, 2026-10-27), and there is no Schedule button to tell it apart from.
 * "All shorts … to your accounts/socials" read as the whole library, and as accounts the user hasn't
 * connected (0 of 4 on scope); this one 4 of 4, true with nothing connected (reading test, 2026-10-28). */
export const AUTO_POST_SUBMIT_SUMMARY = "Schedules every short from this video";

/** Both actual bookings and estimates use the browser's calendar day, including DST. */
const formatAutoPostDay = (date: Date, now: Date) =>
  isSameDay(date, now) ? "today" : isSameDay(date, addDays(now, 1)) ? "tomorrow" : format(date, "MMM d");

export type AutoPostQueue = {
  upcoming: number;
  nextPostAt?: Date | string | null;
};

/** Background waiting is separate from loading settings or saving a preference. */
export const isAutoPostWaitingForShorts = ({ enabled, taskStatus, upcoming = 0, hasQueuedPosts = false }: {
  enabled?: boolean;
  taskStatus?: string;
  upcoming?: number;
  hasQueuedPosts?: boolean;
}): boolean => enabled === true && taskStatus === "processing" && !hasQueuedPosts && !(upcoming > 0);

/** The same queue facts for a one-line status or a two-line tooltip. */
export function getAutoPostQueueText(queue: AutoPostQueue, now = new Date()) {
  if (!Number.isFinite(queue.upcoming) || queue.upcoming <= 0) return null;
  const compactCountLabel = `${queue.upcoming} upcoming`;
  const countLabel = `${compactCountLabel} ${queue.upcoming === 1 ? "post" : "posts"}`;
  const date = queue.nextPostAt ? new Date(queue.nextPostAt) : null;
  const nextPost = date && Number.isFinite(date.getTime())
    ? `Next: ${formatAutoPostDay(date, now)} at ${format(date, "h:mm a")}`
    : null;
  return { countLabel, compactCountLabel, nextPost, summary: nextPost ? `${nextPost} · ${countLabel}` : countLabel };
}

export const autoPublishQueueStatus = (queue: AutoPostQueue, now = new Date()): string | null => {
  const text = getAutoPostQueueText(queue, now);
  return text?.nextPost ? text.summary : null;
};

/** Confirmed bookings remain visible even when a destination needs reconnecting.
 * Empty scheduled queues do not prove all posts have been published. */
export const autoPublishStatus = (state: AutoPublishState, accounts: SocialProviderAccount[], taskStatus?: string, now = new Date()) => {
  if (!state.enabled) return null;
  const queue = getAutoPostQueueText(state, now)?.summary;
  if (queue) return queue;
  if (!state.accountIds.some((id) => accounts.some((account) => account.id === id))) return null;
  return taskStatus === "ready" ? AUTO_POST_STATUS.empty
    : taskStatus === "processing"
      ? autoPublishFirstPost(state, accounts, now) ?? AUTO_POST_STATUS.waitingForShorts
      : null;
};

/** Loading starts with the feature description; saves keep the last confirmed status. */
export const getAutoPublishDisplayStatus = ({ state, accounts, taskStatus, shortCount, isStarting = false }: {
  state: AutoPublishState | null;
  accounts: SocialProviderAccount[];
  taskStatus?: string;
  shortCount?: number;
  isStarting?: boolean;
}, now = new Date()): string =>
  !state || isStarting ? autoPublishSummary(shortCount)
    : autoPublishStatus(state, accounts, taskStatus, now) ?? autoPublishSummary(shortCount);

// Processing (p90 under 25 min, 2026-10-26) plus the planner's 30-minute lead.
const FIRST_POST_MARGIN_MS = 60 * 60 * 1000;
const FIRST_POST_SCAN_DAYS = 14;

/** Existing projects retain their zone until saved; each day's slot resolves across DST. */
const firstSlot = (cadence: AccountCadence, pool: number[], earliest: number, zone: string) => {
  const hours = getPreferredHoursForCadence(pool, cadence.maxPostsPerDay);
  const firstDay = dayjs(earliest).tz(zone).format("YYYY-MM-DD");
  for (let index = 0; index < FIRST_POST_SCAN_DAYS; index++) {
    const day = dayjs.utc(firstDay).add(index, "day");
    if (!cadence.allowedISODays.includes(day.day() || 7)) continue;
    const slot = hours.map(hour => dayjs.tz(`${day.format("YYYY-MM-DD")} ${String(hour).padStart(2, "0")}:00`, zone))
      .find(at => at.valueOf() >= earliest);
    if (slot) return slot;
  }
  return null;
};

/** Estimate this project's actual next slot, displayed in browser-local time like the calendar. */
export const autoPublishFirstPost = (state: AutoPublishState, accounts: SocialProviderAccount[], now = new Date()) => {
  const cadences = new Map(Object.entries(state.cadences));
  const zone = state.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const earliest = Math.max(now.getTime() + FIRST_POST_MARGIN_MS, state.startAt?.getTime() ?? 0);
  const picks = state.accountIds.filter(id => accounts.some(account => account.id === id)).map(id => getAccountCadence(cadences, id));
  const slots = picks.map(cadence => firstSlot(cadence, state.preferredHours ?? DEFAULT_PREFERRED_HOURS, earliest, zone))
    .filter((slot): slot is dayjs.Dayjs => slot !== null);
  if (slots.length === 0) return null;
  const first = new Date(Math.min(...slots.map(slot => slot.valueOf())));
  const dayLabel = formatAutoPostDay(first, now);
  return `First post ${dayLabel} around ${format(first, first.getMinutes() === 0 ? "h a" : "h:mm a")}`;
};
